import { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { Modal } from 'components/Modal';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { UltraSelect } from 'components/UltraSelect';
import { siteSource } from 'data/entity-sources/sources';

import { addDaysToDate, formatDateAsAnniversary, getDaysToNextWeekday } from 'helpers/dateTimes';

import { deliveriesUpdatedHandler } from '../utils/deliverySubscriptionsHandlers';

import { SiteState, useCreateDeliveryForSiteMutation } from 'data/__generated__';

// A store delivery is driven from totops: only the sites operated there can get one.
const TOTOPS_SITE_SOURCE = siteSource.withScope({
    states: [SiteState.Regular],
    types: null,
    shouldOperateOnTotops: true,
});

export function CreateSiteDeliveryPopup({
    isOpen,
    setIsOpen,
    defaultDate,
    onCreated,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    defaultDate?: Date;
    onCreated?: () => void;
}): JSX.Element {
    const [siteId, setSiteId] = useState<string | null>(null);
    const [deliveryDate, setDeliveryDate] = useState<Date>(
        () => defaultDate ?? addDaysToDate({ date: new Date(), daysToAdd: getDaysToNextWeekday(new Date().getDay()) }),
    );

    const [createDeliveryForSite, { loading }] = useCreateDeliveryForSiteMutation();

    async function handleSubmit() {
        if (!siteId) return;

        try {
            const { data } = await createDeliveryForSite({
                variables: { siteId, deliveryDate: formatDateAsAnniversary({ dateTime: deliveryDate }) },
            });

            if (data) {
                deliveriesUpdatedHandler([data.createDeliveryForSite]);
                onCreated?.();
                toast.success('Livraison créée !');
                setSiteId(null);
                setIsOpen(false);
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Une erreur est survenue lors de la création');
        }
    }

    return (
        <Modal title="Créer une livraison store" isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <UltraSelect source={TOTOPS_SITE_SOURCE} label="Choisir un site" value={siteId} onChange={setSiteId} />
            <DateContainer>
                <TotemDatePicker
                    label="Date de livraison"
                    selected={deliveryDate}
                    onChange={(date) => date && setDeliveryDate(date as Date)}
                />
            </DateContainer>
            <SubmitContainer>
                <TotemPrimaryButton onClick={handleSubmit} disabled={!siteId || loading}>
                    {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Confirmer'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </Modal>
    );
}

const DateContainer = styled.div`
    margin-top: 15px;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
